import { Button, Grid, Typography } from '@material-ui/core';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '../../../../Session';
import { UserType } from '../../../../api';
import OutlinedInputField from '../../../../components/fields/OutlinedInputField';
import DealerSelection from './DealerSelection';
import SelectedDealersList from './SelectedDealersList';
import TagSelection from './TagSelection';
import useInviteQuoteData from './hooks/useInviteQuoteData';
import useInviteQuoteSelection from './hooks/useInviteQuoteSelection';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        actionButton: {
            width: 240,
            maxWidth: '100%',
            marginLeft: 'auto',
            marginRight: 'auto',
            [theme.breakpoints.up('sm')]: {
                marginLeft: 0,
                marginRight: 0,
            },
        },
        actionButtonContainer: {
            display: 'flex',
            justifyContent: 'center',
            [theme.breakpoints.up('sm')]: {
                justifyContent: 'flex-start',
            },
        },
    })
);

export type InviteQuoteProps = {
    inSessionDealerIds?: string[];
    inSessionTagIds?: string[];
    ignoreInSessionFilter?: boolean;
    displayDealerTagNames?: boolean;
    onSubmit?: () => void;
};

const InviteQuote = ({
    inSessionDealerIds,
    inSessionTagIds,
    ignoreInSessionFilter,
    displayDealerTagNames,
    onSubmit,
}: InviteQuoteProps) => {
    const { t } = useTranslation(['purchaseTransactionsPage', 'common']);
    const currentUser = useCurrentUser();
    // Admin and ValuationTeam users should see the valuationTeamView, while Approver users should see the approverView.
    const viewKey = currentUser.type === UserType.Approver ? 'approverView' : 'valuationTeamView';
    const styles = useStyles();

    const { dealers, tags } = useInviteQuoteData();
    const { availableTags, availableDealers, onDealerSelectionChange, onTagSelectionChange, selectedDealers } =
        useInviteQuoteSelection({
            dealers,
            ignoreInSessionFilter,
            inSessionTagIds,
            inSessionDealerIds,
            tags,
        });

    return (
        <Grid spacing={2} container>
            <Grid xs={12} item>
                <OutlinedInputField
                    color="secondary"
                    label={t('quotation.mainInfo.remarks')}
                    name="remarks"
                    placeholder={t('quotation.mainInfo.enterDescription')}
                    multiline
                />
            </Grid>
            <Grid lg={6} md={6} sm={12} xs={12} item>
                <TagSelection onSelectionChange={onTagSelectionChange} tags={availableTags} />
            </Grid>
            <Grid lg={6} md={6} sm={12} xs={12} item>
                <DealerSelection dealers={availableDealers} onSelectionChange={onDealerSelectionChange} />
            </Grid>
            <Grid xs={12} item>
                <SelectedDealersList displayDealerTagNames={displayDealerTagNames} selectedDealers={selectedDealers} />
                <Grid className={styles.actionButtonContainer} style={{ marginTop: '6px' }} xs={12} container item>
                    <Button
                        className={styles.actionButton}
                        color="secondary"
                        onClick={onSubmit}
                        type="submit"
                        variant="contained"
                    >
                        {t(`purchaseTransactionsPage:quotation.inviteQuote.${viewKey}.actionTitle`)}
                    </Button>
                </Grid>
                <Grid style={{ marginTop: '6px' }} xs={12} item>
                    <Typography color="textSecondary" variant="caption">
                        {t(`purchaseTransactionsPage:quotation.inviteQuote.${viewKey}.inviteNote`)}
                    </Typography>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default InviteQuote;
